import { cloneDeep } from 'lodash';
import { ref, type Ref, reactive, computed } from 'vue';
import { graphql } from '@/gql';
import _ from 'lodash';

import { queryExecute } from '@/Utils/graphqlAPI';
import { gogoInternalNumbers } from '@/constants/app';
import type { PartnerRegistrationDataType } from '@/models/partner';
import type { Announcement } from '@/models/announcement';
import type { OperatorType } from '@/models/operator';
import type { TemporaryNote } from '@/models/temporaryNote';

import { operatorService } from '@/services/operators';
import { outboundPhoneNumberService } from '@/services/outboundPhoneNumber';

const isDialogOpenedFromRightPanel = ref(false);
const locationDataToSave: Ref<any> = ref(null);
const formDataObj: Ref<{ [pageId: string]: any }> = ref({});
const instacartEmail = ref('');
const serviceAccountId = ref('');
const needLoveState = reactive({
  complaintType: '',
  autoFillRide: null as any,
});
const smsBoardState = reactive({
  newMessage: null as any,
  newRoomPhoneNumber: '',
});
const conferenceSid = ref('');
const operatorActivityDetailCondition = ref(null as any);
const blacklistAddresses = ref([]);
const temporaryNotes: Ref<TemporaryNote[]> = ref([]);
const teamLeads: Ref<OperatorType[]> = ref([]);
const supervisors: Ref<OperatorType[]> = ref([]);
const operators: Ref<OperatorType[]> = ref([]);
const isAllowExpandingOnHover = ref(true);
const activeAnnouncements: Ref<Announcement[]> = ref([]);
const isCalling = ref(false);
const isOnActiveCall = ref(false);
const partnerRegistrationData: Ref<PartnerRegistrationDataType | null> = ref(null);
const incomingCallDetail: Ref<string | null> = ref(null);
const dynamicOutboundPhoneNumbers: Ref<string[]> = ref([]);

export const useGlobal = (pageId?: string) => {
  const lastFormData = computed(() => {
    if (!pageId) {
      return null;
    }

    return formDataObj.value[pageId];
  });

  const teamLeadsAndSupsList = computed(() => {
    const list = [];
    for (const teamLead of teamLeads.value) {
      list.push({
        id: teamLead.id,
        name: teamLead.fullName,
      });
    }

    for (const supervisor of supervisors.value) {
      list.push({
        id: supervisor.id,
        name: supervisor.fullName,
      });
    }

    const uniqueData = _.uniqBy(list, 'id');
    const orderedData = _.orderBy(uniqueData, ['name'], ['asc']);

    return orderedData;
  });

  const gogoNumbers = computed(() => [...gogoInternalNumbers, ...dynamicOutboundPhoneNumbers.value]);

  const setFormData = (data: any) => {
    if (!pageId) {
      return;
    }

    formDataObj.value[pageId] = data;
  };

  const clearFormData = () => {
    formDataObj.value = {};
  };

  const setDialogOpenedFromRightPanel = (flag: boolean) => {
    isDialogOpenedFromRightPanel.value = flag;
  };

  const setLocationDataToSave = (value: any) => {
    locationDataToSave.value = value;
  };

  const setInstacartEmail = (email: string) => {
    instacartEmail.value = email;
  };

  const setServiceAccountId = (id: string) => {
    serviceAccountId.value = id;
  };

  const setComplaintType = (complaintType: string) => {
    needLoveState.complaintType = complaintType;
  };

  const setAutoFillRide = (ride: any) => {
    needLoveState.autoFillRide = cloneDeep(ride);
  };

  const setNewMessage = (newMessage: any) => {
    smsBoardState.newMessage = cloneDeep(newMessage);
  };

  const setNewRoomPhoneNumber = (phoneNumber: string) => {
    smsBoardState.newRoomPhoneNumber = phoneNumber;
  };

  const setConferenceSid = (value: string) => {
    conferenceSid.value = value;
  };

  const setOperatorActivityDetailCondition = (value: any) => {
    operatorActivityDetailCondition.value = cloneDeep(value);
  };

  const setBlacklistAddresses = (value: any) => {
    blacklistAddresses.value = cloneDeep(value);
  };

  const setTemporaryNotes = (value: any) => {
    temporaryNotes.value = cloneDeep(value);
  };

  const setAllowExpandingOnHover = (value: boolean) => {
    isAllowExpandingOnHover.value = value;
  };

  const verifyEmail = async (email: string) => {
    const response = await queryExecute({
      query: graphql(`
        query EmailVerification($email: String!) {
          emailVerification(email: $email) {
            result
            reason
            role
            free
            disposable
            accept_all
            did_you_mean
            sendex
            email
            user
            domain
            success
          }
        }
      `),
      variables: {
        email,
      },
      fetchPolicy: 'no-cache',
    });

    return response.data.emailVerification;
  };

  const setActiveAnnouncements = (data: Announcement[]) => {
    activeAnnouncements.value = data;
  };

  const setIsCalling = (value: boolean) => {
    isCalling.value = value;
  };

  const setIsOnActiveCall = (value: boolean) => {
    isOnActiveCall.value = value;
  };

  const setPartnerRegistrationData = (value: PartnerRegistrationDataType | null) => {
    partnerRegistrationData.value = value;
  };

  const setIncomingCallDetail = (detail: string | null) => {
    incomingCallDetail.value = detail;
  };

  const getTeamLeads = async () => {
    teamLeads.value = [];
    try {
      const response = await operatorService.fetchTeamLeads({
        pagination: {
          offset: 0,
          limit: 100,
        },
      });

      teamLeads.value = response.rows.map((item: OperatorType) => ({
        ...item,
        fullName: `${item.firstName} ${item.lastName}`,
      }));

      teamLeads.value = _.orderBy(teamLeads.value, ['fullName'], ['asc']);
    } catch (err) {
      // No need to do anything
    }
  };

  /*
    This function is recursive function
    because the API returns response regarding with pagination limit and
    we don't know total count, but we need to get all supervisors
  */
  const getSupervisors = async (offset: number = 0) => {
    const paginationLimit = 100;
    if (offset === 0) {
      supervisors.value = [];
    }
    try {
      const response = await operatorService.fetchSupervisors({
        pagination: {
          offset,
          limit: paginationLimit,
        },
      });

      const page = response.rows.map((item: OperatorType) => ({
        ...item,
        fullName: `${item.firstName} ${item.lastName}`,
      }));

      supervisors.value = _.orderBy([...supervisors.value, ...page], ['fullName'], ['asc']);

      if (response.rows.length === paginationLimit) {
        await getSupervisors(offset + paginationLimit);
      }
    } catch (err) {
      // No need to do anything
    }
  };

  const getOperators = async (offset: number = 0) => {
    const limit = 100;
    try {
      const response = await operatorService.fetchOperators({
        isActive: true,
        pagination: {
          offset,
          limit,
        },
      });

      for (const operator of response.rows) {
        operators.value.push(operator);
      }

      if (response.rows.length === limit) {
        await getOperators(offset + limit);
      }
    } catch (error) {
      // No need to do anything
    }
  };

  const getAllOperators = async () => {
    await getOperators();

    operators.value = operators.value.map((item) => ({
      ...item,
      fullName: `${item.firstName} ${item.lastName}`,
    }));

    operators.value = _.orderBy(operators.value, ['fullName'], ['asc']);
  };

  const getOutboundPhoneNumbers = async () => {
    try {
      dynamicOutboundPhoneNumbers.value = await outboundPhoneNumberService.getUniqueOutboundPhoneNumbers();
    } catch {
      dynamicOutboundPhoneNumbers.value = [];
    }
  };

  return {
    lastFormData,
    isDialogOpenedFromRightPanel,
    locationDataToSave,
    instacartEmail,
    serviceAccountId,
    needLoveState,
    smsBoardState,
    conferenceSid,
    operatorActivityDetailCondition,
    blacklistAddresses,
    temporaryNotes,
    teamLeadsAndSupsList,
    isAllowExpandingOnHover,
    activeAnnouncements,
    isCalling,
    isOnActiveCall,
    partnerRegistrationData,
    incomingCallDetail,
    teamLeads,
    operators,
    supervisors,
    gogoNumbers,

    setFormData,
    clearFormData,
    setDialogOpenedFromRightPanel,
    setLocationDataToSave,
    setInstacartEmail,
    setServiceAccountId,
    setComplaintType,
    setAutoFillRide,
    setNewMessage,
    setNewRoomPhoneNumber,
    setConferenceSid,
    setOperatorActivityDetailCondition,
    setBlacklistAddresses,
    setTemporaryNotes,
    verifyEmail,
    setAllowExpandingOnHover,
    setActiveAnnouncements,
    setIsCalling,
    setIsOnActiveCall,
    setPartnerRegistrationData,
    setIncomingCallDetail,
    getTeamLeads,
    getSupervisors,
    getAllOperators,
    getOutboundPhoneNumbers,
  };
};
